import React, { useState, useEffect } from "react";
import {
  Row,
  Form,
  InputGroup,
  Button,
  Spinner,
  Alert,
  Modal,
} from "react-bootstrap";
import { FaSearch, FaPlus, FaUserCheck, FaUserSlash } from "react-icons/fa";
import CoachCard from "./CoachCard";
import InviteCoachModal from "./InviteCoachModal";
import ApproveCoachesModal from "./ApproveCoachesModal";
import DisabledCoachesModal from "./DisabledCoachesModal";
import CoachDetailsModal from "./CoachDetailsModal";
import {
  approveCoach,
  enableCoach,
  disableCoach,
  rejectCoach,
  db,
} from "../../../../backend/firebaseFunctions";
import { collection, onSnapshot, doc, getDoc } from "firebase/firestore";
import CustomAlert from "../../Robuts Components/CustomAlert";
import {
  GOLD_PACKAGE,
  SILVER_PACKAGE,
  BRONZE_PACKAGE,
} from "../../../../backend/PAYMENT_PACKAGES_CONSTANTS";
import "./CoachesPane.css";
import "./CoachDetailsModal.css";
import Joyride, { STATUS } from "react-joyride";
import { FaQuestionCircle } from "react-icons/fa";

const CoachesPane = ({ coaches, setCoaches, activateAlert, userData }) => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDisabledModal, setShowDisabledModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDisableConfirmModal, setShowDisableConfirmModal] = useState(false);
  const [showPackageLimitModal, setShowPackageLimitModal] = useState(false);
  const [packageLimit, setPackageLimit] = useState(0);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [modalPosition, setModalPosition] = useState(null);
  const [alertState, setAlertState] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
  });
  const [loadingStates, setLoadingStates] = useState({
    approve: false,
    enable: false,
    disable: false,
    search: false,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const organizationCode = userData?.userOrganization?.organizationCode;
  const [runTour, setRunTour] = useState(false);
  // Joyride steps for CoachesPane
  const joyrideSteps = [
    {
      target: ".coaches-joyride-header",
      content:
        "ברוכים הבאים למסך המאמנים! כאן תוכלו לנהל את כל המאמנים בארגון.",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: '[data-tour="invite-coach-button"]',
      content: "הזמינו מאמן חדש לארגון בלחיצה כאן.",
      placement: "bottom",
    },
    {
      target: '[data-tour="approve-coaches-button"]',
      content: "אשרו מאמנים שממתינים לאישור דרך כפתור זה.",
      placement: "bottom",
    },
    {
      target: '[data-tour="disabled-coaches-button"]',
      content: "צפו במאמנים שהושבתו והפעילו אותם מחדש במידת הצורך.",
      placement: "bottom",
    },
    {
      target: ".modern-search-input",
      content: "חפשו מאמנים לפי שם, אימייל או טלפון.",
      placement: "bottom",
    },
    {
      target: ".modern-coach-card",
      content: "כאן תמצאו את רשימת כל המאמנים הפעילים בארגון.",
      placement: "top",
    },
    {
      target: '[data-tour="coaches-help-button"]',
      content: "לחצו כאן בכל עת כדי להפעיל שוב את הסיור המודרך!",
      placement: "left",
      spotlightClicks: true,
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
    }
  };

  const tourButtonStyle = {
    position: "absolute",
    top: "20px",
    left: "20px",
    backgroundColor: "#1976d2",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    zIndex: 100,
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  };

  const handleTourButtonClick = () => {
    setRunTour(true);
  };

  useEffect(() => {
    const coachesCollectionRef = collection(
      db,
      "users",
      userData?.userId,
      "coaches"
    );

    const unsub = onSnapshot(coachesCollectionRef, async (snapshot) => {
      const changes = snapshot.docChanges();
      if (changes.length === coaches.length) return;
      const newCoachChanges = changes.filter(
        (change) => change.type === "added"
      );
      if (newCoachChanges.length === 0) return;

      let newCoach = newCoachChanges[0].doc.data();

      // Fetch the coach's user data
      const userDocRef = doc(db, "users", newCoach.coachUserID);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        newCoach = {
          ...newCoach,
          userData: userDoc.data(),
        };
      }

      setCoaches((prev) => [...prev, newCoach]);
    });

    return () => {
      unsub();
    };
  }, [coaches]);

  const hasViewPrivilege =
    userData?.userType === "manager" ||
    userData?.coachData?.accessPrivilege?.coaches?.view ||
    (userData?.userType === "coach" && !userData?.coachData) || // Default allow if coachData is missing
    false;
  const hasEditPrivilege =
    userData?.userType === "manager" ||
    userData?.coachData?.accessPrivilege?.coaches?.write ||
    (userData?.userType === "coach" && !userData?.coachData) || // Default allow if coachData is missing
    false;

  // --- DESIGN STYLES ---
  const mainBg = {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f8fafc 0%, #e3e9f7 100%)",
    padding: "32px 0",
  };
  const containerStyle = {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 16px",
  };
  const headerStyle = {
    fontWeight: 800,
    fontSize: "2.5rem",
    letterSpacing: 1,
    marginBottom: 24,
    textAlign: "center",
  };
  const sectionCardStyle = {
    marginBottom: 40,
    background: "#fff",
    borderRadius: 18,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    padding: 24,
    direction: "ltr",
  };
  const addButtonStyle = {
    fontWeight: 700,
    fontSize: "1.2rem",
    borderRadius: 999,
    padding: "16px 40px",
    boxShadow: "0 2px 8px rgba(0,180,80,0.10)",
    marginBottom: 32,
    textAlign: "center",
    alignContent: "center",
    alignItems: "center",
    justifyContent: "center",
    display: "flex",
    flexDirection: "row",
    gap: 16,
    flexWrap: "wrap",
  };
  // --- END DESIGN STYLES ---

  // If user doesn't have view privilege, show message and return
  if (!hasViewPrivilege) {
    return (
      <div style={mainBg}>
        <div style={containerStyle}>
          <div className="coaches-wrapper">
            <Alert
              variant="warning"
              className="m-3"
              style={{ direction: "rtl" }}
            >
              אין לך הרשאות לצפייה במאמנים. אנא פנה למנהל המערכת לקבלת הרשאות.
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  // Calculate number of coaches waiting for approval
  const calcNumberOfWaitingCoaches = () => {
    return (
      coaches?.filter(
        (coach) => coach?.userData?.userStatus === "waitingForOrgAccept"
      )?.length || 0
    );
  };

  // Calculate number of disabled coaches
  const calcNumberOfDisabledCoaches = () => {
    return (
      coaches?.filter((coach) => coach?.userData?.userStatus === "disabled")
        ?.length || 0
    );
  };

  const filteredCoaches = coaches?.filter((coach) => {
    if (searchTerm === "") {
      return true; // Show all coaches, we'll handle disabled state in the UI
    }
    const searchTermLower = searchTerm?.toLowerCase();
    return (
      coach?.userData?.userName?.toLowerCase().includes(searchTermLower) ||
      coach?.userData?.userEmailAddress
        ?.toLowerCase()
        .includes(searchTermLower) ||
      coach?.userData?.userPhoneNumber?.includes(searchTermLower)
    );
  });

  // Calculate if a coach should be disabled based on package limits
  const isCoachDisabled = (coach) => {
    if (coach?.userData?.userStatus === "active") {
      const userPackage = userData?.paymentDetails?.plan?.toLowerCase();
      let maxCoaches;

      switch (userPackage) {
        case "gold":
          maxCoaches = GOLD_PACKAGE.NUMBER_OF_COACHES;
          break;
        case "silver":
          maxCoaches = SILVER_PACKAGE.NUMBER_OF_COACHES;
          break;
        case "bronze":
          maxCoaches = BRONZE_PACKAGE.NUMBER_OF_COACHES;
          break;
        default:
          maxCoaches = 0;
      }

      const coachIndex = coaches.findIndex(
        (c) => c.coachUserID === coach.coachUserID
      );

      // Disable coaches that exceed the package limit
      return coachIndex >= maxCoaches;
    }
    return false;
  };

  const handleCardClick = (coach, position) => {
    setSelectedCoach(coach);
    setModalPosition(position);
    setShowDetailsModal(true);
  };

  const handleApproveCoach = async (coach) => {
    setLoadingStates((prev) => ({ ...prev, approve: true }));
    try {
      await approveCoach(coach.coachUserID, userData?.userId);

      // Update local state
      const updatedCoaches = coaches.map((c) =>
        c.coachUserID === coach.coachUserID
          ? { ...c, userData: { ...c.userData, userStatus: "active" } }
          : c
      );
      setCoaches(updatedCoaches);

      // Show success message
      setAlertState({
        show: true,
        type: "success",
        title: "אישור מאמן",
        message: `המאמן ${coach.userData?.userName} אושר בהצלחה`,
      });
    } catch (error) {
      console.error("Error approving coach:", error);
      setAlertState({
        show: true,
        type: "error",
        title: "שגיאה באישור מאמן",
        message: "אירעה שגיאה באישור המאמן",
      });
    } finally {
      setLoadingStates((prev) => ({ ...prev, approve: false }));
    }
  };

  const handleApproveCoachesClick = () => {
    // Check package limits before showing approve modal
    const userPackage = userData?.paymentDetails?.plan?.toLowerCase();
    let maxCoaches;

    switch (userPackage) {
      case "gold":
        maxCoaches = GOLD_PACKAGE.NUMBER_OF_COACHES;
        break;
      case "silver":
        maxCoaches = SILVER_PACKAGE.NUMBER_OF_COACHES;
        break;
      case "bronze":
        maxCoaches = BRONZE_PACKAGE.NUMBER_OF_COACHES;
        break;
      default:
        maxCoaches = 0;
    }

    setPackageLimit(maxCoaches);
    const activeCoaches = coaches.filter(
      (c) => c?.userData?.userStatus === "active"
    ).length;
    if (activeCoaches >= maxCoaches) {
      setShowPackageLimitModal(true);
      return;
    }

    setShowApproveModal(true);
  };

  const handleEnableCoach = async (coach) => {
    setLoadingStates((prev) => ({ ...prev, enable: true }));
    try {
      await enableCoach(coach.coachUserID, userData?.userId);

      // Update local state
      const updatedCoaches = coaches.map((c) =>
        c.coachUserID === coach.coachUserID
          ? { ...c, userData: { ...c.userData, userStatus: "active" } }
          : c
      );
      setCoaches(updatedCoaches);

      // Show success message
      setAlertState({
        show: true,
        type: "success",
        title: "הפעלת מאמן",
        message: `המאמן ${coach.userData?.userName} הופעל בהצלחה`,
      });
    } catch (error) {
      console.error("Error enabling coach:", error);
      setAlertState({
        show: true,
        type: "error",
        title: "שגיאה בהפעלת מאמן",
        message: "אירעה שגיאה בהפעלת המאמן",
      });
    } finally {
      setLoadingStates((prev) => ({ ...prev, enable: false }));
    }
  };

  const handleDisableCoach = async (coach) => {
    setLoadingStates((prev) => ({ ...prev, disable: true }));
    try {
      await disableCoach(coach.coachUserID, userData?.userId);

      // Update local state
      const updatedCoaches = coaches.map((c) =>
        c.coachUserID === coach.coachUserID
          ? { ...c, userData: { ...c.userData, userStatus: "disabled" } }
          : c
      );
      setCoaches(updatedCoaches);

      // Show success message
      setAlertState({
        show: true,
        type: "success",
        title: "השבתת מאמן",
        message: `המאמן ${coach.userData?.userName} הושבת בהצלחה`,
      });
    } catch (error) {
      console.error("Error disabling coach:", error);
      setAlertState({
        show: true,
        type: "error",
        title: "שגיאה בהשבתת מאמן",
        message: "אירעה שגיאה בהשבתת המאמן",
      });
    } finally {
      setLoadingStates((prev) => ({ ...prev, disable: false }));
      setShowDetailsModal(false);
      setShowDisableConfirmModal(false);
    }
  };

  const handleRejectCoach = async (coach) => {
    setLoadingStates((prev) => ({ ...prev, approve: true }));
    try {
      await rejectCoach(userData?.userId, coach.coachUserID);

      // Update local state - remove from coaches list since they're no longer in the owner's coaches collection
      const updatedCoaches = coaches.filter(
        (c) => c.coachUserID !== coach.coachUserID
      );
      setCoaches(updatedCoaches);

      // Show success message
      setAlertState({
        show: true,
        type: "success",
        title: "דחיית מאמן",
        message: `המאמן ${coach.userData?.userName} נדחה בהצלחה`,
      });
    } catch (error) {
      console.error("Error rejecting coach:", error);
      setAlertState({
        show: true,
        type: "error",
        title: "שגיאה בדחיית מאמן",
        message: "אירעה שגיאה בדחיית המאמן",
      });
    } finally {
      setLoadingStates((prev) => ({ ...prev, approve: false }));
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Show loading state
    setLoadingStates((prev) => ({ ...prev, search: true }));

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout
    const timeout = setTimeout(() => {
      setLoadingStates((prev) => ({ ...prev, search: false }));
    }, 500);

    setSearchTimeout(timeout);
  };

  const renderCoachesList = () => {
    if (
      !coaches ||
      coaches.filter(
        (c) =>
          c?.userData?.userStatus !== "disabled" &&
          c?.userData?.userStatus !== "waitingForOrgAccept"
      ).length === 0
    ) {
      return (
        <div className="coaches-empty-state">
          <h3>אין מאמנים פעילים במערכת</h3>
        </div>
      );
    }

    if (filteredCoaches?.length === 0) {
      return (
        <div className="coaches-empty-state">
          <p>לא נמצאו מאמנים התואמים את החיפוש</p>
        </div>
      );
    }

    const activeCoaches = filteredCoaches?.filter(
      (c) =>
        c?.userData?.userStatus !== "disabled" &&
        c?.userData?.userStatus !== "waitingForOrgAccept"
    );

    return (
      <div>
        {activeCoaches.map((coach) => (
          <div key={coach.id}>
            <CoachCard
              coach={coach}
              onCardClick={handleCardClick}
              isDisabled={isCoachDisabled(coach)}
            />
          </div>
        ))}
      </div>
    );
  };

  // Modern empty state if no coaches
  const hasAnyCoaches =
    coaches &&
    coaches.filter(
      (c) =>
        c?.userData?.userStatus !== "disabled" &&
        c?.userData?.userStatus !== "waitingForOrgAccept"
    ).length > 0;

  return (
    <div style={mainBg}>
      <Joyride
        steps={joyrideSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        disableScrolling={false}
        scrollToFirstStep={true}
        scrollOffset={100}
        callback={handleJoyrideCallback}
        styles={{
          options: {
            arrowColor: "#fff",
            backgroundColor: "#fff",
            overlayColor: "rgba(0, 0, 0, 0.5)",
            primaryColor: "#1976d2",
            textColor: "#333",
            zIndex: 1000,
          },
          tooltipContainer: {
            direction: "rtl",
            textAlign: "right",
          },
          buttonNext: {
            backgroundColor: "#1976d2",
            fontSize: 14,
            padding: "8px 16px",
          },
          buttonBack: {
            marginRight: 10,
            fontSize: 14,
            padding: "8px 16px",
          },
          buttonSkip: {
            color: "#666",
            fontSize: 14,
            padding: "8px 16px",
          },
        }}
        locale={{
          back: "הקודם",
          close: "סגור",
          last: "סיום",
          next: "הבא",
          skip: "דלג",
        }}
      />
      <button
        style={tourButtonStyle}
        onClick={handleTourButtonClick}
        data-tour="coaches-help-button"
        aria-label="סיור מודרך"
      >
        <FaQuestionCircle size={22} />
      </button>
      <style>{`
        .modern-coach-card {
          border-radius: 16px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
          background: #fff !important;
          margin-bottom: 24px;
        }
        .modern-section-header-icon {
          font-size: 1.3em;
          margin-left: 8px;
        }
        .modern-add-btn {
          border-radius: 999px !important;
          font-weight: 700 !important;
          font-size: 1.2rem !important;
          padding: 16px 40px !important;
          box-shadow: 0 2px 8px rgba(0,180,80,0.10) !important;
        }
        .modern-modal .modal-content {
          border-radius: 18px !important;
          background: #f8fafc !important;
        }
        .modern-modal .modal-header {
          border-radius: 18px 18px 0 0 !important;
        }
        .modern-modal .modal-footer {
          border-radius: 0 0 18px 18px !important;
        }
        .modern-form-input, .modern-form-input:focus {
          border-radius: 10px !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04) !important;
          border: 1px solid #e0e0e0 !important;
          background: #fff !important;
        }
        .modern-coaches-table {
          border-radius: 16px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
          background: #fff !important;
          padding: 16px;
        }
        .modern-search-input {
          border-radius: 999px !important;
          font-size: 1.1rem !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04) !important;
          border: 1px solid #e0e0e0 !important;
          background: #fff !important;
          padding: 12px 24px !important;
          width: 80%;
        }
        .notification-badge {
          position: absolute;
          top: -22px;
          right: 60px;
          background: #ff5252;
          color: #fff;
          border-radius: 50%;
          min-width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1em;
          font-weight: bold;
          box-shadow: 0 1px 4px rgba(0,0,0,0.12);
          z-index: 2;
        }
        .coach-btn-icon-badge-wrapper {
          position: relative;
          display: inline-block;
          margin-left: 8px;
        }
      `}</style>
      <div style={containerStyle}>
        <h1 style={headerStyle} className="coaches-joyride-header">
          מאמנים
        </h1>
        {hasEditPrivilege && (
          <div
            style={{
              textAlign: "center",
              marginBottom: 32,
              display: "flex",
              flexDirection: "row-reverse",
              justifyContent: "center",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="primary"
              className="modern-add-btn d-flex align-items-center gap-2"
              style={addButtonStyle}
              onClick={() => setShowInviteModal(true)}
              data-tour="invite-coach-button"
            >
              <FaPlus /> הזמן מאמן
            </Button>
            <Button
              variant="warning"
              className="modern-add-btn d-flex align-items-center gap-2"
              style={addButtonStyle}
              onClick={handleApproveCoachesClick}
              disabled={loadingStates.approve}
              data-tour="approve-coaches-button"
            >
              <span className="coach-btn-icon-badge-wrapper">
                {loadingStates.approve ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <FaUserCheck />
                )}
                {calcNumberOfWaitingCoaches() > 0 && (
                  <span className="notification-badge">
                    {calcNumberOfWaitingCoaches()}
                  </span>
                )}
              </span>
              אישור מאמנים
            </Button>
            <Button
              variant="secondary"
              className="modern-add-btn d-flex align-items-center gap-2"
              style={addButtonStyle}
              onClick={() => setShowDisabledModal(true)}
              disabled={loadingStates.enable}
              data-tour="disabled-coaches-button"
            >
              <span className="coach-btn-icon-badge-wrapper">
                {loadingStates.enable ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <FaUserSlash />
                )}
                {calcNumberOfDisabledCoaches() > 0 && (
                  <span className="notification-badge">
                    {calcNumberOfDisabledCoaches()}
                  </span>
                )}
              </span>
              מאמנים מושבתים
            </Button>
          </div>
        )}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <InputGroup
            className="modern-search-input"
            style={{ maxWidth: "100vw", margin: "0 auto 24px auto" }}
          >
            <Form.Control
              placeholder="חיפוש מאמנים..."
              value={searchTerm}
              onChange={handleSearch}
              className=" text-end"
              dir="rtl"
              style={{ marginRight: "1vw" }}
            />
            <InputGroup.Text>
              {loadingStates.search ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FaSearch />
              )}
            </InputGroup.Text>
          </InputGroup>
        </div>
        {/* Empty state if no coaches at all */}
        {!hasAnyCoaches && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 320,
              color: "#888",
              fontWeight: 600,
              fontSize: "1.3rem",
              background: "#fff",
              borderRadius: 18,
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              margin: "48px auto 0 auto",
              maxWidth: 500,
              padding: 48,
              direction: "rtl",
            }}
          >
            <FaUserCheck
              size={64}
              style={{ marginBottom: 24, color: "#1976d2", opacity: 0.7 }}
            />
            לחץ למעלה כדי להזמין את המאמן הראשון שלך!
          </div>
        )}
        {/* Coaches Table Section */}
        {hasAnyCoaches && (
          <div style={sectionCardStyle} className="modern-coach-card">
            <div
              style={{
                fontWeight: 700,
                fontSize: "1.4rem",
                color: "#1976d2",
                borderRight: "6px solid #1976d2",
                paddingRight: 16,
                marginBottom: 24,
                display: "flex",
                alignItems: "center",
                gap: 10,
                direction: "rtl",
              }}
            >
              <FaUserCheck className="modern-section-header-icon" />
              כל המאמנים
            </div>
            <div>
              <Row xs={1} className="g-4 flex-column">
                {renderCoachesList()}
              </Row>
            </div>
          </div>
        )}
        {/* Modals and Alerts */}
        {alertState.show && (
          <CustomAlert
            type={alertState.type}
            title={alertState.title}
            message={alertState.message}
          />
        )}
        <Modal
          show={showPackageLimitModal}
          onHide={() => setShowPackageLimitModal(false)}
        >
          <Modal.Header className="hebrewHeader">
            <Modal.Title className="hebrewText">אופס!</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="hebrewText">
              הגעת למקסימום המאמנים שניתן לנהל בחבילה שלך ({packageLimit}{" "}
              מאמנים)
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowPackageLimitModal(false)}
            >
              הבנתי
            </Button>
          </Modal.Footer>
        </Modal>
        <InviteCoachModal
          userData={userData}
          show={showInviteModal}
          onHide={() => setShowInviteModal(false)}
          organizationCode={organizationCode}
        />
        <ApproveCoachesModal
          show={showApproveModal}
          onHide={() => setShowApproveModal(false)}
          coaches={coaches}
          onApproveCoach={handleApproveCoach}
          onRejectCoach={handleRejectCoach}
          loading={loadingStates.approve}
        />
        <DisabledCoachesModal
          show={showDisabledModal}
          onHide={() => setShowDisabledModal(false)}
          coaches={coaches}
          onEnableCoach={handleEnableCoach}
          loading={loadingStates.enable}
        />
        <CoachDetailsModal
          show={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          coach={selectedCoach}
          triggerPosition={modalPosition}
          onDisableCoach={hasEditPrivilege ? handleDisableCoach : null}
          loading={loadingStates.disable}
          hasEditPrivilege={hasEditPrivilege}
          setCoaches={setCoaches}
          showDisableConfirmModal={showDisableConfirmModal}
          setShowDisableConfirmModal={setShowDisableConfirmModal}
        />
      </div>
    </div>
  );
};

export default CoachesPane;
